import React, { SyntheticEvent, useCallback, useRef } from "react";
import { connect } from "react-redux";

import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import { EntityType, GuideType } from "@mapmycustomers/shared/enum";
import { getPreviewEntityUniqueElementId, stopEvents } from "@mapmycustomers/shared/util/browser";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";

import MmcNotificationAction from "@app/enum/MmcNotificationAction";
import { showEntityView } from "@app/store/entityView/actions";
import { isOnlyMentions } from "@app/store/notification";
import { clickOnNotification, hideNotifications } from "@app/store/notification/actions";
import { RootState } from "@app/store/rootReducer";
import MmcNotification, { MmcNotificationEntity } from "@app/types/MmcNotification";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useGuides from "@app/util/contexts/useGuides";
import { formatFriendlyRelativeTime } from "@app/util/formatters";
import getFormattedDate from "@app/util/notifications/getFormattedDate";

import Actions from "./component/Actions/Actions";
import Description from "./component/Description";
import Icon from "./component/Icon";
import Title from "./component/Title";
import styles from "./NotificationCommon.module.scss";
import getNotificationActionAnalyticGroup from "./util/getNotificationActionAnalyticGroup";

interface NotificationProps {
  notification: MmcNotification;
  onGoToSettings: () => void;
  tabIndex: number;
}

const Notification = ({
  isOnlyMentions,
  notification,
  onClickOnNotification,
  onGoToSettings,
  onHideNotifications,
  onShowEntityView,
  tabIndex,
}: NotificationProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();
  const ref = useRef<HTMLDivElement>(null);
  const [hovered, setHoveredAsTrue, setHoveredAsFalse] = useBoolean();
  const analyticsIssuer = useAnalytics();

  const handleEntityClick = useCallback(
    (entity: MmcNotificationEntity, event?: SyntheticEvent<HTMLElement>) => {
      if (notification.action !== MmcNotificationAction.MENTION) {
        stopEvents(event);
        onShowEntityView({
          elementId: event?.currentTarget
            ? getPreviewEntityUniqueElementId(event.currentTarget)
            : undefined,
          entityId: entity.id,
          entityType: entity.type,
        });

        if (entity.type !== EntityType.ACTIVITY) {
          onHideNotifications();
        }
      }
    },
    [onHideNotifications, notification, onShowEntityView]
  );

  const { showGuide } = useGuides();

  const handleClick = useCallback(() => {
    // Starting a guide needs the React-side guides service, so it can't move into the saga.
    // Only a web guide has a runner here — a mobile one is left to the saga, which sends the
    // user to Onboarding rather than letting the click do nothing.
    const defaultGuide = notification.isDefaultGuideAssignment
      ? notification.guides?.[0]
      : undefined;
    if (defaultGuide?.type === GuideType.WEB) {
      showGuide(defaultGuide.frigadeFlowId, { mode: "requested" });
    }
    onClickOnNotification({
      elementId: getPreviewEntityUniqueElementId(ref.current as HTMLElement),
      notification,
    });
    analyticsIssuer.clicked(["Notification"], {
      fromMentions: !!isOnlyMentions,
      group: getNotificationActionAnalyticGroup(notification.action),
    });
  }, [notification, analyticsIssuer, isOnlyMentions, showGuide, onClickOnNotification]);

  return (
    <div
      className={cn(styles.container, { [styles.unread]: !notification.readStatus })}
      onClick={handleClick}
      onMouseEnter={setHoveredAsTrue}
      onMouseLeave={setHoveredAsFalse}
      ref={ref}
      tabIndex={tabIndex}
    >
      <div className={styles.iconWrapper}>
        <Icon notification={notification} />
      </div>
      <div className={styles.mainPartWrapper}>
        <Description notification={notification} onEntityClick={handleEntityClick} />
        <Title notification={notification} />
        <Tooltip placement="bottom" title={getFormattedDate(intl, notification)}>
          <span className={styles.date}>
            {formatFriendlyRelativeTime(intl, notification.updatedAt)}
          </span>
        </Tooltip>
      </div>
      <div>
        <Actions hovered={hovered} notification={notification} onGoToSettings={onGoToSettings} />
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isOnlyMentions: isOnlyMentions(state),
});

const mapDispatchToProps = {
  onClickOnNotification: clickOnNotification,
  onHideNotifications: hideNotifications,
  onShowEntityView: showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps)(Notification);
